// Chat: the players at an online table (and in a 2v2 game's lobby) can type to each other. The game server keeps
// the last messages and sends them to anyone who sits down (or comes back); people watching read along, but can't
// write. Used by the 2v2 lobby (menu.ts) and the table (table/tableScreen.ts).
import { el, button } from './ui.ts';
import { CHAT_MAX, type ChatLine } from '../shared/online.ts';
import type { OnlineConnection } from './table/connection.ts';

export interface ChatBox {
  root: HTMLElement;
  /** puts the cursor in the message box */
  focus(): void;
  /** stops listening (the box can't be used again) */
  dispose(): void;
}

const timeOf = (at: number) => new Date(at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

/**
 * A chat box for one online game: what has been said, and a line to type in. `onNew` hears about each message
 * someone else says (for an unread count while the box is out of sight).
 */
export function chatBox(conn: OnlineConnection, opts: { readOnly?: boolean; onNew?: (line: ChatLine) => void } = {}): ChatBox {
  const list = el('div', { class: 'chat-list', role: 'log', 'aria-live': 'polite' });
  const empty = el('p', { class: 'chat-empty muted' }, opts.readOnly ? 'Nothing has been said yet.' : 'Nothing has been said yet. Say hello!');
  const input = el('input', { class: 'chat-input', type: 'text', maxlength: String(CHAT_MAX), placeholder: opts.readOnly ? 'Only the players can write here' : 'Type a message…', 'aria-label': 'Chat message', autocomplete: 'off', disabled: !!opts.readOnly }) as HTMLInputElement;
  const send = () => {
    const text = input.value.trim();
    if (!text) return;
    conn.sendChat(text);
    input.value = '';
  };
  const sendBtn = button('Send', send, 'btn-small btn-primary chat-send', opts.readOnly ? { disabled: true } : {});
  input.addEventListener('keydown', (e) => {
    e.stopPropagation(); // (typing here never sets off the table's keyboard shortcuts)
    if (e.key === 'Enter') { e.preventDefault(); send(); }
    if (e.key === 'Escape') input.blur();
  });
  input.addEventListener('keyup', (e) => e.stopPropagation());
  const form = el('div', { class: 'chat-form' }, input, sendBtn);
  const root = el('div', { class: 'chat' }, empty, list, form);

  const lineEl = (l: ChatLine) =>
    // (written so it reads right even before the page's styles have loaded: "Ana: hello"; the time it was said
    // shows only when the mouse rests on the message)
    el(
      'div',
      { class: `chat-line ${l.seat === conn.seat && !conn.spectating ? 'is-me' : ''}`, title: timeOf(l.at) },
      el('span', { class: 'chat-who' }, `${l.name}:`),
      el('span', { class: 'chat-text' }, ` ${l.text}`),
    );
  const draw = () => {
    list.replaceChildren(...conn.chat.map(lineEl));
    empty.hidden = conn.chat.length > 0;
    list.scrollTop = list.scrollHeight;
  };
  draw();
  const stop = conn.onChat((line) => {
    if (!line) return draw();
    const near = list.scrollHeight - list.scrollTop - list.clientHeight < 40;
    list.append(lineEl(line));
    while (list.children.length > conn.chat.length) list.firstElementChild?.remove();
    empty.hidden = true;
    if (near || line.seat === conn.seat) list.scrollTop = list.scrollHeight;
    if (line.seat !== conn.seat || conn.spectating) opts.onNew?.(line);
  });
  return { root, focus: () => input.focus(), dispose: stop };
}
